// src/components/About.jsx
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const sectionParent = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.7, // Increased to make animations play one after another
      delayChildren: 0.1,
    },
  },
};

const imageSlide = {
  hidden: { opacity: 0, x: -80, scale: 1.08 },
  show: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const headingVariant = {
  hidden: { opacity: 0, y: 40, skewY: 3 },
  show: {
    opacity: 1,
    y: 0,
    skewY: 0,
    transition: {
      duration: 0.9,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const subheadingVariant = {
  hidden: { opacity: 0, x: 30 },
  show: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: "easeOut",
    },
  },
};

const paragraphVariant = {
  hidden: { 
    opacity: 1,
    clipPath: "circle(0% at 50% 50%)"
  },
  show: {
    opacity: 1,
    clipPath: "circle(85% at 50% 50%)",
    transition: {
      duration: 1.5,
      clipPath: {
        type: "spring",
        stiffness: 70,
        damping: 24
      },
    }
  }
};

const highlightBadge = {
  hidden: { opacity: 0, scale: 0.7, y: 10 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 220,
      damping: 18,
      delay: 0.2,
    },
  },
};

export default function About({ noMargin }) {
  const imageRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"]
  });
  
  // Zoom effect: scales from 1.0 to 1.3 as section scrolls through viewport
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.3]);

  return (
    <section className=" min-h-screen w-full bg-[var(--bg2)] flex" style={noMargin ? undefined : { marginTop: "90px" }}>
      {/* Left image with scroll zoom */}
      <motion.div
        ref={imageRef}
        variants={imageSlide}
        initial="hidden"
        whileInView="show"
        viewport={{ once: false, amount: 0.3 }} // allow animation on every scroll into view
        className="hidden md:block md:w-1/2 lg:w-[52%] overflow-hidden"
      >
        <motion.div className="h-full w-full" layout style={{ scale: imageScale }}>
          <motion.img
            src="./images/about_bg.png"
            alt="Handyman standing in a house"
            className="h-full w-full object-cover"
            whileHover={{ scale: 1.04 }}
            transition={{ type: "spring", stiffness: 160, damping: 18 }}
          />
        </motion.div>
      </motion.div>

      {/* Right content */}
      <motion.div
        variants={sectionParent}
        initial="hidden"
        whileInView="show"
        viewport={{ once: false, amount: 0.3 }} // allow animation on every scroll into view
        className="w-full md:w-1/2 lg:w-[48%] flex items-center"
      >
        <div className="px-6 sm:px-10 lg:px-20 py-16 max-w-xl">
          {/* Small badge / label */}
          <motion.div
            variants={highlightBadge}
            className="inline-flex items-center rounded-full bg-[var(--bg)] px-3 py-1 mb-5 text-[11px] uppercase tracking-[0.16em] text-white"
          >
            Multi-Sector Expertise
          </motion.div>

          {/* Main heading */}
          <motion.p
            variants={headingVariant}
            className="text-[46px] leading-[0.9] sm:text-[64px] font-semibold tracking-tight text-white/90"
          >
            Who We <br /> Are
          </motion.p>

          {/* Subheading */}
          <motion.p
            variants={subheadingVariant}
            className="mt-5 text-lg font-semibold text-white/90 tracking-tight"
          >
            About Skyline MEP Consultants
          </motion.p>

          {/* Body copy */}
          <motion.p
            variants={paragraphVariant}
            className="mt-6 text-[15px] leading-relaxed text-white/80"
          >
            Skyline MEP Consultants delivers innovative, end-to-end MEP solutions for residential, commercial, and industrial projects. <br /><br />
            Our expert team blends technical excellence with creative problem-solving, ensuring every system is efficient, safe, and future-ready. <br /><br />
            We partner closely with clients and architects to bring visions to life—on time, on budget, and to the highest standards.
          </motion.p>
        </div>
      </motion.div>
    </section>
  );
}
